/* GrowMax Studio: progressive enhancement; native scrolling and content remain usable. */
(() => {
  'use strict';
  if (window.GrowMaxStudio) return;
  const body = document.body;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const mobile = matchMedia('(max-width: 749px)');
  const editor = body.classList.contains('gm-editor') || Boolean(window.Shopify?.designMode);
  const cleanups = new Map();
  const allowed = () => !editor && !reduced.matches && body.dataset.gmMotionEnabled === 'true' && (!mobile.matches || body.dataset.gmMotionMobile === 'true');
  const elements = (scope, selector) => [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('gm-in-view');
        observer.unobserve(entry.target);
      }
    });
  }, {threshold: 0.04, rootMargin: '0px 0px 30px 0px'}) : null;

  function reveal(scope) {
    elements(scope, '[data-gm-motion]').forEach(element => {
      observer?.unobserve(element);
      element.removeAttribute('data-gm-reveal-ready');
      const requested = element.dataset.gmMotion;
      const type = !requested || requested === 'inherit' ? body.dataset.gmReveal : requested;
      // Avoid stacking a section reveal with every child reveal. Explicit child
      // choices still work; inherited children use the section's entrance.
      const insideSection = !element.hasAttribute('data-gm-section') && element.closest('[data-gm-section]');
      if (!allowed() || !observer || !type || type === 'none' || (insideSection && (!requested || requested === 'inherit'))) return;
      element.dataset.gmRevealReady = type;
      observer.observe(element);
    });
  }

  function marquee(container) {
    const track = container.querySelector('[data-gm-track]');
    const toggle = container.parentElement.querySelector('[data-gm-marquee-toggle]');
    if (!track || !toggle || container.dataset.enabled !== 'true' || !allowed()) return () => {};
    const children = [...track.children].filter(child => child.tagName !== 'STYLE' && getComputedStyle(child).display !== 'none');
    if (!children.length) return () => {};
    container.classList.add('is-running');
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = children.reduce((sum, child) => sum + child.getBoundingClientRect().width + gap, 0);
    if (!distance) { container.classList.remove('is-running'); return () => {}; }
    const clones = [];
    const copies = Math.max(1, Math.ceil(container.clientWidth / distance));
    const visualProperties = ['background','color','border','border-radius','padding','font-size','font-family','font-weight','letter-spacing','width'];
    for (let index = 0; index < copies; index++) {
      for (const original of children) {
        const clone = original.cloneNode(true);
        clone.setAttribute('aria-hidden','true');
        clone.setAttribute('inert','');
        clone.dataset.gmClone = 'true';
        for (const node of [clone, ...clone.querySelectorAll('*')]) {
          node.removeAttribute('id'); node.removeAttribute('data-shopify-editor-block');
          node.removeAttribute('data-gm-block'); node.removeAttribute('data-gm-motion');
          if (node.matches('a,button,input,select,textarea,[tabindex]')) node.setAttribute('tabindex','-1');
        }
        clone.querySelectorAll('style,script').forEach(node => node.remove());
        const computed = getComputedStyle(original);
        visualProperties.forEach(property => clone.style.setProperty(property, computed.getPropertyValue(property)));
        track.append(clone); clones.push(clone);
      }
    }
    const reverse = container.dataset.direction === 'right';
    const frames = [{transform:`translateX(${reverse ? -distance : 0}px)`},{transform:`translateX(${reverse ? 0 : -distance}px)`}];
    const animation = track.animate(frames,{duration:distance / Math.max(10, Number(container.dataset.speed) || 30) * 1000,iterations:Infinity,easing:'linear'});
    let paused = false;
    function sync() {
      const temporaryPause = document.hidden || container.matches(':hover') || container.contains(document.activeElement);
      if (paused || temporaryPause) animation.pause(); else animation.play();
      toggle.setAttribute('aria-pressed',String(paused));
      toggle.textContent = paused ? 'Play strip' : 'Pause strip';
    }
    const click = () => {paused = !paused; sync();};
    toggle.hidden = false; toggle.addEventListener('click',click);
    ['pointerenter','pointerleave','focusin','focusout'].forEach(type => container.addEventListener(type,sync));
    document.addEventListener('visibilitychange',sync); sync();
    return () => {
      animation.cancel(); clones.forEach(clone => clone.remove());
      container.classList.remove('is-running'); toggle.hidden = true;
      toggle.removeEventListener('click',click);
      ['pointerenter','pointerleave','focusin','focusout'].forEach(type => container.removeEventListener(type,sync));
      document.removeEventListener('visibilitychange',sync);
    };
  }

  function unload(scope) {
    for (const [element, cleanup] of cleanups) {
      if (element === scope || scope.contains(element)) {cleanup(); cleanups.delete(element);}
    }
    elements(scope,'[data-gm-motion]').forEach(element => observer?.unobserve(element));
  }
  function init(scope = document) {
    unload(scope); reveal(scope);
    elements(scope,'[data-gm-marquee]').forEach(element => cleanups.set(element,marquee(element)));
  }

  const cursor = document.querySelector('[data-gm-cursor]');
  function hideCursor() {if (cursor) cursor.hidden = true; body.classList.remove('gm-native-hidden');}
  if (cursor) {
    document.addEventListener('pointermove',event => {
      if (editor || reduced.matches || !fine.matches || event.pointerType === 'touch' || event.target.closest('input,textarea,[contenteditable="true"]')) {hideCursor(); return;}
      // A missing cursor image falls back to the ring, never an invisible cursor.
      if (cursor.dataset.style === 'image' && !cursor.querySelector('img')) cursor.dataset.style = 'ring';
      cursor.hidden = false;
      cursor.style.transform = `translate3d(${event.clientX}px,${event.clientY}px,0)`;
      cursor.classList.toggle('is-link',Boolean(event.target.closest('a,button,summary,[role="button"]')));
      body.classList.toggle('gm-native-hidden',cursor.dataset.hideNative === 'true');
    },{passive:true});
    document.addEventListener('pointerout',event => {if (!event.relatedTarget) hideCursor();});
    window.addEventListener('blur',hideCursor);
    document.addEventListener('keydown',hideCursor);
  }

  const control = document.querySelector('[data-gm-page-scroll]');
  const label = control?.querySelector('[data-gm-scroll-label]');
  let playing = false, frame = 0, previousTime = 0, scrollPosition = 0;
  const scrollAllowed = () => control && !editor && !reduced.matches && (!mobile.matches || control.dataset.mobile === 'true');
  function stop() {
    playing = false; cancelAnimationFrame(frame); frame = 0; previousTime = 0;
    if (control) {control.setAttribute('aria-pressed','false'); label.textContent = control.dataset.play;}
  }
  function tick(now) {
    if (!playing || !scrollAllowed() || document.hidden) {stop(); return;}
    const dt = previousTime ? Math.min(now-previousTime,50) : 0; previousTime = now;
    const end = document.documentElement.scrollHeight - innerHeight;
    if (scrollY >= end-2) {stop(); return;}
    scrollPosition = Math.min(end,scrollPosition + dt * (Number(control.dataset.speed)||24)/1000);
    window.scrollTo({top:scrollPosition,behavior:'instant'});
    frame = requestAnimationFrame(tick);
  }
  function start() {
    if (!scrollAllowed() || document.documentElement.scrollHeight - innerHeight <= scrollY+2) return;
    playing = true; previousTime = 0; scrollPosition = scrollY;
    control.setAttribute('aria-pressed','true'); label.textContent = control.dataset.pause;
    frame = requestAnimationFrame(tick);
  }
  function syncScrollControl() {if(control) control.hidden = !scrollAllowed(); if(!scrollAllowed()) stop();}
  control?.addEventListener('click',() => {if(playing) stop(); else start();});
  ['wheel','touchstart'].forEach(type => window.addEventListener(type,stop,{passive:true}));
  document.addEventListener('pointerdown',event => {if(!event.target.closest('[data-gm-page-scroll]')) stop();},{passive:true});
  document.addEventListener('keydown',event => {if(['Tab','Escape','ArrowDown','ArrowUp','PageDown','PageUp','Home','End',' '].includes(event.key)) stop();});
  document.addEventListener('focusin',event => {if(event.target.matches('input,textarea,select,[contenteditable="true"]')) stop();});
  document.addEventListener('visibilitychange',() => {if(document.hidden){stop();hideCursor();}});

  const progress = document.querySelector('[data-gm-progress]');
  const top = document.querySelector('[data-gm-top]');
  let pendingProgress = false;
  function updateProgress() {
    pendingProgress = false;
    const height = document.documentElement.scrollHeight - innerHeight;
    if(progress) progress.style.transform = `scaleX(${height > 0 ? Math.min(1,scrollY/height) : 0})`;
    if(top) top.hidden = editor || scrollY < 400;
  }
  window.addEventListener('scroll',() => {if(!pendingProgress){pendingProgress=true;requestAnimationFrame(updateProgress);}},{passive:true});
  top?.addEventListener('click',() => {stop();window.scrollTo({top:0,behavior:reduced.matches || body.dataset.gmSmooth !== 'true' ? 'instant' : 'smooth'});});

  document.addEventListener('shopify:section:load',event => {init(event.target);stop();});
  document.addEventListener('shopify:section:unload',event => {unload(event.target);stop();});
  document.addEventListener('shopify:section:reorder',() => {stop();updateProgress();});
  document.addEventListener('shopify:block:select',event => {
    stop(); event.target.closest('details')?.setAttribute('open','');
    elements(event.target,'[data-gm-reveal-ready]').forEach(element=>element.classList.add('gm-in-view'));
  });
  document.addEventListener('click',event => {
    document.querySelectorAll('.gm-nav-dropdown[open]').forEach(details => {if(!details.contains(event.target)) details.open=false;});
  });
  document.addEventListener('keydown',event => {if(event.key==='Escape') document.querySelectorAll('.gm-nav-dropdown[open]').forEach(details=>{details.open=false;details.querySelector('summary')?.focus();});});

  let resizeTimer;
  window.addEventListener('resize',() => {clearTimeout(resizeTimer);resizeTimer=setTimeout(()=>{init();syncScrollControl();updateProgress();hideCursor();},160);});
  reduced.addEventListener('change',() => {init();syncScrollControl();hideCursor();});
  fine.addEventListener('change',hideCursor);
  init();syncScrollControl();updateProgress();
  if (control?.dataset.autostart === 'true') start();
  window.GrowMaxStudio = Object.freeze({version:'1.2.1',refresh:init,pause:stop});
})();
